import type { ReactNode } from 'react';
import { useNavigate, useParams } from 'react-router';
import { PROJECT_PERMISSIONS } from '@inference.engine/permissions/web';
import { useHasProjectPermission } from '@/entities/user';
import { PanelReveal } from '@/shared/ui/PanelReveal';
import { ROUTES } from '@/shared/config/routes';
import { ApprovalFolderRail } from '../ApprovalFolderRail';
import { useIsRailCollapsed } from '../../model';

interface ApprovalLayoutProps {
  projectId: string;
  onCreateFolder: () => void;
  children: ReactNode;
}

/**
 * The approval section: folder tree on the left, whatever the route resolves to
 * on the right.
 *
 * Picking a folder navigates rather than holding selection in state, so the URL
 * stays the one source of truth — a link to a folder still opens it, and the
 * rail reads which one is open from the same place. Both the list and the
 * detail route render the rail through here, so the section always has its
 * navigation — but they are sibling route elements, so React remounts this
 * layout on the hop between them. Which open branches the tree is showing is
 * held in `approval-rail.store` for exactly that reason.
 */
export function ApprovalLayout(props: ApprovalLayoutProps) {
  const navigate = useNavigate();
  const { folderId } = useParams<{ folderId: string }>();
  const canCreate = useHasProjectPermission([PROJECT_PERMISSIONS.ProjectUploadAssets]);
  const isCollapsed = useIsRailCollapsed();

  return (
    <div className="flex min-h-0 flex-1">
      <PanelReveal
        isOpen={!isCollapsed}
        axis="width"
        gapEdge="right"
        fadeOnClose
        className="flex shrink-0"
      >
        <ApprovalFolderRail
          projectId={props.projectId}
          selectedFolderId={folderId}
          onSelectFolder={(folder) =>
            void navigate(ROUTES.PROJECT_FOLDER_DETAIL(props.projectId, folder.id))
          }
          onCreateFolder={canCreate ? props.onCreateFolder : undefined}
        />
      </PanelReveal>

      <div className="flex min-h-0 min-w-0 flex-1 flex-col gap-app-inset">{props.children}</div>
    </div>
  );
}
